//#region [IMPORT]

import React, { createContext, useEffect, useState } from 'react';

import { onRemoveTaskQueueItem, onTaskQueueInfo, onUpdateTaskQueueInfo, removeListener, requestTask, subscribeTaskQueueInfo, unsubscribeTaskQueueInfo } from 'api/prokey/prokey.websocket';

// === STYLE / MATERIAL UI ===
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import {
  Box,
  Typography,
} from '@mui/material';
import './TaskList.css';
import { useTheme } from '@mui/material/styles';

// === UTILS ===
import { getHexAlpha } from 'components/styles.utils';

//#endregion

function CustomToolbar() {
  return (
    <GridToolbarContainer sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
      </Box>
      <GridToolbarQuickFilter
        debounceMs={300}
        sx={{
          minWidth: 320,
          '& .MuiInputBase-root': {
            height: 40,
            fontSize: '0.95rem',
          },
        }}
      />
    </GridToolbarContainer>
  );
}

function NoRowsOverlay() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <Typography variant="body2" color="text.secondary">No tasks available in your assigned queues</Typography>
    </Box>
  );
}

//-----------------------------------------------------------------
export function TaskList() {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const theme = useTheme();

  const STORAGE_KEY = 'tasklist-dgv-state';

  const loadPersistedState = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  };

  const persisted = loadPersistedState();

  const [paginationModel, setPaginationModel] = useState(
    persisted.paginationModel || { page: 0, pageSize: 25 }
  );

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    persisted.columnVisibilityModel || {}
  );

  const [filterModel, setFilterModel] = useState(
    persisted.filterModel || { items: [] }
  );

  const [tasks, setTasks] = useState({});
  const [searchInput, setSearchInput] = useState("")

  const has_data = !!tasks.modules;

  //#endregion

  //#region [STYLES]

  const _styles = {
    container: {
      height: '100%',
      width: '100%',
      padding: '1rem',
      overflow: 'auto',
      overflowY: 'hidden',
    },
    container2: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      padding: '0.5rem',
      overflow: 'auto',
      overflowY: 'hidden',
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
    },
    title: {
      padding: '1rem',
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
      gridColumn: 1 / 4,
    },
    table_container: {
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
      height: 'calc( 100% - 4.5rem )',
      overflowY: 'auto',
    },
    table: {
    },
    table_header_cell: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
    table_cell: {
      borderBottom: 'none',
    },
    search_container: {
      display: 'flex',
      padding: '0.5rem',
      justifyContent: 'flex-start',
      width: '100%',
      gridColumn: '2 / span 2',
    },
    search_text: {
      width: '100%',
    },
    card: {
      display: 'grid',
      gap: '0.5rem',
      'gridTemplateColumns': 'repeat(  1, 1fr )',
      'gridTemplateRows': 'max-content 1fr',
      height: 'calc( 100% - 2rem )',
      width: 'calc( 100% - 2rem )',
      maxHeight: '100%',
      maxWidth: '100%',
      overflowY: 'hidden',
      marginTop: '1rem',
      marginLeft: '1rem',
      marginRight: '1rem',
      border: '1px solid ' + theme.palette.background.primary[theme.palette.mode],
      borderRadius: '1rem',
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
    },
    // table header styles not used with DataGrid
  }

  // === ATTRIBUTES

  const
    CONTAINER = {
      sx: _styles.container,
    },
    CONTAINER2 = {
      sx: _styles.container2,
    },
    TITLE = {
      sx: _styles.title,
      variant: 'h5',
      color: theme.palette.primary.main,
      children: ['Task Queue Info'],
    },
    SEARCH_CONTAINER = {
      sx: _styles.search_container,
      flexGrow: 1,
    },
    SEARCH_TEXT = {
      sx: _styles.search_text,
      label: 'Search ID...',
      size: 'small',
      onChange: handleChange,
      name: 'search_task_id_input',
    };


  //#endregion

  //#region [EFFECT]

  useEffect(() => {
    //-----------------------------------------------------------------
    function handleQueueCreate(data) {
      //-----------------------------------------------------------------
      setTasks(data);
    }

    onTaskQueueInfo(handleQueueCreate);

    subscribeTaskQueueInfo();

    return () => {
      unsubscribeTaskQueueInfo();
      removeListener('TASK-QUEUE-INFO', handleQueueCreate);
    };
  }, []);


  useEffect(() => {
    const stateToPersist = {
      paginationModel,
      columnVisibilityModel,
      filterModel,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToPersist));
  }, [paginationModel, columnVisibilityModel, filterModel]);

  useEffect(() => {

    //-----------------------------------------------------------------
    function handleQueueUpdate(data) {
      //-----------------------------------------------------------------
      const temp = { ...tasks }
      temp.modules[data.module][data.id] = data;
      setTasks(temp);
    }

    //-----------------------------------------------------------------
    function handleQueueRemove(data) {
      //-----------------------------------------------------------------
      const temp = { ...tasks }
      delete temp.modules[data.module][data.id];
      setTasks(temp);
    }

    onUpdateTaskQueueInfo(handleQueueUpdate);
    onRemoveTaskQueueItem(handleQueueRemove);

    return () => {
      removeListener('TASK-QUEUE-INFO-UPDATE', handleQueueUpdate);
      removeListener('TASK-QUEUE-INFO-REMOVE', handleQueueRemove);
    };
  }, [tasks]);

  //#endregion

  //#region [green] Events

  // No longer needed: onDoubleClick for table rows. Use DataGrid's onRowDoubleClick.

  //-----------------------------------------------------------------
  function handleChange(e) {
    //-----------------------------------------------------------------
    e.preventDefault();
    setSearchInput(e.target.value);
  }


  //#endregion

  //#region [METHODS]


  //endregion

  //#region [DISPLAY]

  function beautifyQueueName(value) {
    if (!value) return '';

    return value
      .toLowerCase()
      .replace(/[_-]+/g, ' ')           // underscores & dashes → spaces
      .replace(/\b\w/g, c => c.toUpperCase()); // Capital Case
  }

  // DataGrid columns definition
  const columns = [
    { field: 'priority', headerName: 'Priority', width: 100, sortable: true },
    {
      field: 'module', headerName: 'Queue', width: 140, sortable: true, valueFormatter: (params) =>
        beautifyQueueName(params.value),
    },
    { field: 'task_id', headerName: 'Task ID', flex: 1, sortable: true },
    {
      field: 'start_time', headerName: 'Start Time', width: 180, sortable: true,
      valueFormatter: (params) => new Date(params.value).toLocaleString()
    },
    { field: 'status', headerName: 'Status', width: 140, sortable: true },
    { field: 'user', headerName: 'Prokey User', width: 160, sortable: true },
  ];

  // Build rows from tasks (client-side)
  const rows = has_data ? Object.values(tasks.modules)
    .flatMap(m => Object.values(m))
    .filter(t => t.id.toUpperCase().includes(searchInput.toUpperCase()))
    .map(t => ({
      id: `${t.module}-${t.id}`,
      task_id: t.id,
      priority: t.priority,
      module: t.module,
      start_time: t.start_time,
      status: t.status,
      user: t.user,
    })) : [];

  function display() {
    return (
      <Box {...CONTAINER} >
        <Box {...CONTAINER2}>
          <Typography     {...TITLE} />
        </Box>
        <Box sx={{ height: 'calc(100% - 4.5rem)', width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={setColumnVisibilityModel}
            filterModel={filterModel}
            onFilterModelChange={setFilterModel}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            onRowDoubleClick={(params) => requestTask(params.row.task_id)}
            density="compact"
            slots={{ toolbar: CustomToolbar, noRowsOverlay: NoRowsOverlay }}
            sx={{
              backgroundColor: theme.palette.background.primary[theme.palette.mode],
              border: 'none',

              '& .MuiDataGrid-row:nth-of-type(even)': {
                backgroundColor: getHexAlpha(
                  theme.palette.background.paper,
                  0.25
                ),
              },

              '& .MuiDataGrid-row:nth-of-type(odd)': {
                backgroundColor: getHexAlpha(
                  theme.palette.background.paper,
                  0.1
                ),
              },

              '& .MuiDataGrid-cell:hover': {
                backgroundColor: 'inherit',
              },

              '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                outline: 'none',
              },

              '& .MuiDataGrid-row:hover': {
                backgroundColor: getHexAlpha(
                  theme.palette.primary.main,
                  0.08
                ),
                cursor: 'pointer',
              },

              '& .MuiDataGrid-row.Mui-selected': {
                backgroundColor: getHexAlpha(
                  theme.palette.primary.main,
                  0.15
                ),
              },

              '& .MuiDataGrid-row.Mui-selected:hover': {
                backgroundColor: getHexAlpha(
                  theme.palette.primary.main,
                  0.18
                ),
              },

              '& .MuiDataGrid-overlayWrapper': {
                minHeight: 80,
              },
            }}
          />
        </Box>
      </Box>
    );
  };


  //#endregion

  return display();
}
